import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { SearchableSelect } from './SearchableSelect';
import {
  IMAGE_MAX_KB,
  validateContactDetail,
  validateEmployeeId,
  validateEmail,
  validateAadharNumber,
  validatePanNumber,
  validateImageFilesUnderSize,
  validateDateNotAfterToday,
  validateRequired,
  validateEmployeeName,
  todayDateInputValue,
} from '../../utils/validation';
import {
  useCreateEmployeeMutation,
  useGetActiveDepartmentsQuery,
  useGetActiveDesignationsQuery,
  useGetLocationsByDepartmentQuery,
} from '../../redux/employeeApi';
import { employeeListBackPath } from '../../utils/employeeListState';

const inputClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

const RequiredStar = () => <span className="text-red-600">*</span>;

const LEVEL_TYPE_OPTIONS = [
  { value: 'level1', label: 'Level 1' },
  { value: 'level2', label: 'Level 2' },
  { value: 'level3', label: 'Level 3' },
  { value: 'level4', label: 'Level 4' },
];

const EMPLOYMENT_NATURE_OPTIONS = [
  { value: 'gspl', label: 'GSPL' },
  { value: 'vendor', label: 'Vendor' },
];

const COST_CENTRE_TYPE_OPTIONS = [
  { value: 'restaurant', label: 'Restaurant Operations' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'corporate', label: 'Corporate' },
];

function normalizeEmploymentNature(value, fallbackOutsource) {
  const compact = String(value || '').trim().toLowerCase();
  if (compact === 'gspl' || compact === 'vendor') return compact;
  if (fallbackOutsource === true || fallbackOutsource === 'true') return 'vendor';
  if (fallbackOutsource === false || fallbackOutsource === 'false') return 'gspl';
  return '';
}

function normalizeCostCentreType(value, fallbackProduction) {
  const compact = String(value || '').trim().toLowerCase();
  if (compact === 'restaurant' || compact === 'manufacturing' || compact === 'corporate') {
    return compact;
  }
  if (fallbackProduction === true || fallbackProduction === 'true') return 'manufacturing';
  return '';
}

function normalizeLevelType(value) {
  const compact = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  if (
    compact === 'level1' ||
    compact === 'level2' ||
    compact === 'level3' ||
    compact === 'level4'
  ) {
    return compact;
  }
  return '';
}

function toIdArray(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item == null || item === '') return '';
        if (typeof item === 'object') return String(item.id ?? item.location_id ?? '');
        return String(item);
      })
      .filter((v) => v !== '');
  }
  if (value == null || value === '') return [];
  if (typeof value === 'object') {
    const id = value.id ?? value.location_id;
    return id == null || id === '' ? [] : [String(id)];
  }
  return [String(value)];
}

function designationOptionLabel(d) {
  if (!d || typeof d !== 'object') return '';
  const fromRoles = Array.isArray(d.role_names)
    ? d.role_names
        .map((r) => (typeof r === 'string' ? r : r?.name))
        .filter(Boolean)
        .join(', ')
    : '';
  return (
    d.name ??
    d.designation_name ??
    d.title ??
    (fromRoles || undefined) ??
    String(d.id ?? '')
  );
}

function getInputClass(hasError) {
  return `${inputClasses}${
    hasError ? ' border-red-500 focus:border-red-500 focus:ring-red-500' : ''
  }`;
}


function AdminEmployeeAdd() {
  const { area } = useAuth();
  const isAdmin = area === 'admin';
  const navigate = useNavigate();
  const location = useLocation();
  const listPath = employeeListBackPath(location);
  const [createEmployee, { isLoading }] = useCreateEmployeeMutation();
  const { data: departmentsRes } = useGetActiveDepartmentsQuery();
  const [form, setForm] = useState({
    empId: '',
    empName: '',
    email: '',
    contact: '',
    isReportingSupervisor: false,
    employmentNature: '',
    costCentreType: '',
    levelType: '',
    departmentIds: [],
    roleId: '',
    reportingSupervisor: '',
    locationIds: [],
    dob: '',
    doj: '',
    aadhar: '',
    pan: '',
  });
  const [empImage, setEmpImage] = useState(null);
  const [aadharImage, setAadharImage] = useState(null);
  const [panImage, setPanImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [empPreviewUrl, setEmpPreviewUrl] = useState('');
  const [aadharPreviewUrl, setAadharPreviewUrl] = useState('');
  const [panPreviewUrl, setPanPreviewUrl] = useState('');
  const empImageInputRef = useRef(null);
  const aadharImageInputRef = useRef(null);
  const panImageInputRef = useRef(null);

  const primaryDepartmentId = form.departmentIds[0] || '';
  const primaryLocationId = form.locationIds[0] || '';

  const { data: locationsByDeptRes } = useGetLocationsByDepartmentQuery(
    { accountable_department_id: primaryDepartmentId },
    { skip: !primaryDepartmentId }
  );
  const { data: designationsRes } = useGetActiveDesignationsQuery(form.departmentIds, {
    skip: form.departmentIds.length === 0,
  });

  const locationOptions = useMemo(() => {
    const rows = locationsByDeptRes?.data || [];
    return rows.map((loc) => ({
      value: String(loc.id ?? loc.location_id),
      label: loc.name ?? loc.location_name ?? String(loc.id ?? loc.location_id),
    }));
  }, [locationsByDeptRes?.data]);

  const reportingSupervisorOptions = useMemo(() => {
    const locations = locationsByDeptRes?.data || [];
    if (!form.locationIds.length) return [];
    const selectedSet = new Set(form.locationIds.map(String));
    const byId = new Map();
    for (const loc of locations) {
      if (!selectedSet.has(String(loc.id ?? loc.location_id))) continue;
      const supervisors = Array.isArray(loc.reporting_supervisors)
        ? loc.reporting_supervisors
        : [];
      for (const s of supervisors) {
        const id = String(s?.id ?? '');
        if (!id || byId.has(id)) continue;
        byId.set(id, s);
      }
    }
    return Array.from(byId.values());
  }, [locationsByDeptRes?.data, form.locationIds]);

  const handleChange = (field) => (e) => {
    const raw = e?.target?.value;
    setForm((prev) => {
      if (field === 'isReportingSupervisor') {
        const next = String(raw) === 'true';
        return {
          ...prev,
          isReportingSupervisor: next,
          departmentIds: [],
          locationIds: [],
          roleId: '',
          reportingSupervisor: '',
        };
      }
      if (field === 'departmentIds') {
        const nextIds = toIdArray(raw).slice(0, 1);
        return {
          ...prev,
          departmentIds: nextIds,
          roleId: '',
          locationIds: [],
          reportingSupervisor: '',
        };
      }
      if (field === 'locationIds') {
        const nextIds = prev.isReportingSupervisor
          ? toIdArray(raw)
          : toIdArray(raw).slice(0, 1);
        return {
          ...prev,
          locationIds: nextIds,
          reportingSupervisor: '',
        };
      }
      if (field === 'email')
        return { ...prev, [field]: String(raw).replace(/\s+/g, '').toLowerCase() };
      if (field === 'empId')
        return {
          ...prev,
          [field]: String(raw).replace(/[^A-Za-z0-9/]/g, '').slice(0, 50),
        };
      if (field === 'empName')
        return {
          ...prev,
          [field]: String(raw).replace(/[^A-Za-z ]/g, '').slice(0, 50),
        };
      if (field === 'contact')
        return { ...prev, [field]: String(raw).replace(/[^0-9]/g, '').slice(0, 10) };
      if (field === 'aadhar')
        return { ...prev, [field]: String(raw).replace(/[^0-9]/g, '').slice(0, 12) };
      if (field === 'pan')
        return {
          ...prev,
          pan: String(raw).replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 10),
        };
      return { ...prev, [field]: raw };
    });
    setErrors((prev) => (prev?.[field] ? { ...prev, [field]: '' } : prev));
  };

  const handleFileChange = (field) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const sizeErr = validateImageFilesUnderSize([file]);
    if (sizeErr) {
      toast.error(sizeErr);
      if (field === 'emp' && empImageInputRef.current) empImageInputRef.current.value = '';
      if (field === 'aadhar' && aadharImageInputRef.current)
        aadharImageInputRef.current.value = '';
      if (field === 'pan' && panImageInputRef.current) panImageInputRef.current.value = '';
      return;
    }

    if (field === 'emp') {
      setEmpImage(file);
      setErrors((prev) => ({ ...prev, empImage: '' }));
    } else if (field === 'aadhar') {
      setAadharImage(file);
      setErrors((prev) => ({ ...prev, aadharImage: '' }));
    } else {
      setPanImage(file);
      setErrors((prev) => ({ ...prev, panImage: '' }));
    }
  };

  const handleRemoveEmpImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEmpImage(null);
    if (empImageInputRef.current) empImageInputRef.current.value = '';
  };

  const handleRemoveAadharImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAadharImage(null);
    if (aadharImageInputRef.current) aadharImageInputRef.current.value = '';
  };

  const handleRemovePanImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPanImage(null);
    if (panImageInputRef.current) panImageInputRef.current.value = '';
  };

  const validateForm = () => {
    const next = {};

    next.empId = validateEmployeeId(form.empId);

    next.empName = validateEmployeeName(form.empName);

    next.email = String(form.email || '').trim()
      ? validateEmail(form.email, 'Email id')
      : '';
    next.contact = String(form.contact || '').trim()
      ? validateContactDetail(form.contact)
      : '';

    next.employmentNature = validateRequired(form.employmentNature, 'Employment nature');

    next.departmentIds =
      form.departmentIds.length > 0 ? '' : 'Please enter department';
    next.roleId = validateRequired(form.roleId, 'Role');
    next.locationIds =
      form.locationIds.length > 0
        ? ''
        : form.isReportingSupervisor
          ? 'Please select at least one location'
          : 'Please enter location';
    next.reportingSupervisor = form.isReportingSupervisor
      ? ''
      : validateRequired(form.reportingSupervisor, 'Reporting supervisor');
    next.dob = String(form.dob || '').trim()
      ? validateDateNotAfterToday(form.dob, 'Date of birth')
      : '';
    next.doj = validateRequired(form.doj, 'Date of joining');

    next.aadhar = String(form.aadhar || '').trim()
      ? validateAadharNumber(form.aadhar, 'Aadhar number')
      : '';
    next.pan = String(form.pan || '').trim()
      ? validatePanNumber(form.pan, 'PAN number')
      : '';

    next.empImage = '';
    next.aadharImage = '';
    next.panImage = '';

    const empSizeErr = empImage ? validateImageFilesUnderSize([empImage]) : '';
    const aadharSizeErr = aadharImage
      ? validateImageFilesUnderSize([aadharImage])
      : '';
    const panSizeErr = panImage ? validateImageFilesUnderSize([panImage]) : '';
    if (empSizeErr) next.empImage = empSizeErr;
    if (aadharSizeErr) next.aadharImage = aadharSizeErr;
    if (panSizeErr) next.panImage = panSizeErr;

    const hasAny = Object.values(next).some(Boolean);
    setErrors(next);
    if (!hasAny) return { ok: true };

    const orderedKeys = [
      'empId',
      'empName',
      'email',
      'contact',
      'employmentNature',
      'costCentreType',
      'departmentIds',
      'roleId',
      'locationIds',
      'reportingSupervisor',
      'dob',
      'doj',
      'aadhar',
      'pan',
      'empImage',
      'aadharImage',
      'panImage',
    ];
    const first =
      orderedKeys.map((k) => next[k]).find(Boolean) ||
      Object.values(next).find(Boolean) ||
      '';

    return { ok: false, firstError: String(first || '').trim() };
  };

  useEffect(() => {
    if (!empImage) {
      setEmpPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(empImage);
    setEmpPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [empImage]);

  useEffect(() => {
    if (!aadharImage) {
      setAadharPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(aadharImage);
    setAadharPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [aadharImage]);

  useEffect(() => {
    if (!panImage) {
      setPanPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(panImage);
    setPanPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [panImage]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const validation = validateForm();
    if (!validation.ok) {
      toast.error(
        validation.firstError || `Please fix the highlighted fields (Images max ${IMAGE_MAX_KB}KB).`
      );
      return;
    }

    const role_id = form.roleId ? Number(form.roleId) : undefined;
    const departmentIds = form.departmentIds.map(Number).filter((n) => Number.isFinite(n));
    const locationIds = form.locationIds.map(Number).filter((n) => Number.isFinite(n));

    const account = {
      employee_id: form.empId,
      name: form.empName,
      email: form.email,
      contact: form.contact,
      is_reporting_supervisor: Boolean(form.isReportingSupervisor),
      employment_nature: normalizeEmploymentNature(form.employmentNature),
      cost_centre_type: normalizeCostCentreType(form.costCentreType),
      is_outsource: normalizeEmploymentNature(form.employmentNature) === 'vendor',
      is_production: normalizeCostCentreType(form.costCentreType) === 'manufacturing',
      level_type: normalizeLevelType(form.levelType) || undefined,
      role_id,
      dob: form.dob || null,
      date_of_joining: form.doj,
      aadhar_no: form.aadhar || undefined,
      pan_number: form.pan.trim().toUpperCase() || undefined,
      status: true,
      employee_image: empImage || undefined,
      aadhar_front_image: aadharImage || undefined,
      pan_image: panImage || undefined,
    };

    account.accountable_department_ids = departmentIds;
    account.location_ids = locationIds;
    account.reporting_supervisor_id = form.reportingSupervisor
      ? Number(form.reportingSupervisor)
      : undefined;

    createEmployee(account)
      .unwrap()
      .then(() => {
        toast.success('Employee created successfully.');
        navigate(listPath);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Create employee failed:', err);
        toast.error(
          err?.data?.message || 'Failed to create employee. Check console for details.'
        );
      });
  };

  return (
    <DashboardLayout title="Add Employee">
      <div className="space-y-4 py-4">
        <div className="flex items-center justify-end gap-4">
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-500">
              <Link
                to={isAdmin ? '/dashboard/admin' : '/dashboard/employee'}
                className="text-brand hover:underline"
              >
                Home
              </Link>
              {' / '}
              <Link to={listPath} className="text-brand hover:underline">
                Employees
              </Link>
              {' / '}
              <span className="text-gray-400">Add Employee</span>
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="rounded-2xl bg-white px-6 py-6 shadow-sm">
          <h3 className="mb-4 text-xl font-semibold text-gray-800">Employee Information</h3>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Employee ID <RequiredStar />
              </span>
              <input
                type="text"
                placeholder="Enter Employee ID"
                value={form.empId}
                onChange={handleChange('empId')}
                maxLength={50}
                className={getInputClass(Boolean(errors.empId))}
              />
              {errors.empId && <span className="mt-1 text-xs text-red-600">{errors.empId}</span>}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Employee Name <RequiredStar />
              </span>
              <input
                type="text"
                placeholder="Enter Name"
                value={form.empName}
                onChange={handleChange('empName')}
                maxLength={50}
                className={getInputClass(Boolean(errors.empName))}
              />
              {errors.empName && (
                <span className="mt-1 text-xs text-red-600">{errors.empName}</span>
              )}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Email Id
              </span>
              <input
                type="email"
                placeholder="Enter Email"
                value={form.email}
                onChange={handleChange('email')}
                className={getInputClass(Boolean(errors.email))}
              />
              {errors.email && <span className="mt-1 text-xs text-red-600">{errors.email}</span>}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Contact Number
              </span>
              <input
                type="text"
                placeholder="Enter Contact Number"
                value={form.contact}
                onChange={handleChange('contact')}
                inputMode="numeric"
                maxLength={10}
                className={getInputClass(Boolean(errors.contact))}
              />
              {errors.contact && (
                <span className="mt-1 text-xs text-red-600">{errors.contact}</span>
              )}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">Is Reporting Supervisor</span>
              <SearchableSelect
                name="isReportingSupervisor"
                value={form.isReportingSupervisor ? 'true' : 'false'}
                onChange={handleChange('isReportingSupervisor')}
                className={getInputClass(false)}
                clearable={false}
                options={[
                  { value: 'false', label: 'No' },
                  { value: 'true', label: 'Yes' },
                ]}
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
              Employment Nature <RequiredStar />
              </span>
              <SearchableSelect
                name="employmentNature"
                value={form.employmentNature}
                onChange={handleChange('employmentNature')}
                placeholder="Select"
                className={getInputClass(Boolean(errors.employmentNature))}
                options={EMPLOYMENT_NATURE_OPTIONS}
              />
              {errors.employmentNature && (
                <span className="mt-1 text-xs text-red-600">{errors.employmentNature}</span>
              )}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
              Cost Centre Type
              </span>
              <SearchableSelect
                name="costCentreType"
                value={form.costCentreType}
                onChange={handleChange('costCentreType')}
                placeholder="Select"
                className={getInputClass(Boolean(errors.costCentreType))}
                options={COST_CENTRE_TYPE_OPTIONS}
              />
              {errors.costCentreType && (
                <span className="mt-1 text-xs text-red-600">{errors.costCentreType}</span>
              )}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">Level Type</span>
              <SearchableSelect
                name="levelType"
                value={form.levelType}
                onChange={handleChange('levelType')}
                placeholder="Select"
                className={getInputClass(false)}
                options={LEVEL_TYPE_OPTIONS}
              />
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
              Accountable Department <RequiredStar />
              </span>
              <SearchableSelect
                name="departmentIds"
                value={form.departmentIds[0] || ''}
                onChange={handleChange('departmentIds')}
                className={getInputClass(Boolean(errors.departmentIds))}
                options={(departmentsRes?.data || []).map((d) => ({
                  value: String(d.id),
                  label:
                    d.name ??
                    d.department_name ??
                    d.title ??
                    d.department?.name ??
                    String(d.id),
                }))}
              />
              {errors.departmentIds && (
                <span className="mt-1 text-xs text-red-600">{errors.departmentIds}</span>
              )}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Role <RequiredStar />
              </span>
              <SearchableSelect
                name="roleId"
                value={form.roleId}
                onChange={handleChange('roleId')}
                className={getInputClass(Boolean(errors.roleId))}
                disabled={form.departmentIds.length === 0}
                options={(designationsRes?.data || []).map((d) => ({
                  value: String(d.id),
                  label: designationOptionLabel(d),
                }))}
              />
              {errors.roleId && (
                <span className="mt-1 text-xs text-red-600">{errors.roleId}</span>
              )}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Location <RequiredStar />
              </span>
              <SearchableSelect
                name="locationIds"
                multiple={form.isReportingSupervisor}
                value={
                  form.isReportingSupervisor
                    ? form.locationIds
                    : form.locationIds[0] || ''
                }
                onChange={handleChange('locationIds')}
                className={getInputClass(Boolean(errors.locationIds))}
                disabled={!primaryDepartmentId}
                options={locationOptions}
              />
              {errors.locationIds && (
                <span className="mt-1 text-xs text-red-600">{errors.locationIds}</span>
              )}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Reporting Supervisor (Manager)
                {!form.isReportingSupervisor ? <RequiredStar /> : null}
              </span>
              <SearchableSelect
                name="reportingSupervisor"
                value={form.reportingSupervisor}
                onChange={handleChange('reportingSupervisor')}
                className={getInputClass(Boolean(errors.reportingSupervisor))}
                disabled={!primaryLocationId}
                options={reportingSupervisorOptions.map((s) => ({
                  value: String(s.id),
                  label: s.name ?? String(s.id),
                }))}
              />
              {errors.reportingSupervisor && (
                <span className="mt-1 text-xs text-red-600">{errors.reportingSupervisor}</span>
              )}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Date of Birth
              </span>
              <div className="relative mt-1">
                <input
                  type="date"
                  value={form.dob}
                  max={todayDateInputValue()}
                  onChange={handleChange('dob')}
                  className={getInputClass(Boolean(errors.dob))}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </span>
              </div>
              {errors.dob && <span className="mt-1 text-xs text-red-600">{errors.dob}</span>}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Date of Joining <RequiredStar />
              </span>
              <div className="relative mt-1">
                <input
                  type="date"
                  value={form.doj}
                  onChange={handleChange('doj')}
                  className={getInputClass(Boolean(errors.doj))}
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </span>
              </div>
              {errors.doj && <span className="mt-1 text-xs text-red-600">{errors.doj}</span>}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                Aadhar Number
              </span>
              <input
                type="text"
                placeholder="0000-0000-0000"
                value={form.aadhar}
                onChange={handleChange('aadhar')}
                className={getInputClass(Boolean(errors.aadhar))}
              />
              {errors.aadhar && <span className="mt-1 text-xs text-red-600">{errors.aadhar}</span>}
            </label>
            <label className="flex flex-col text-sm text-gray-700">
              <span className="inline-flex items-center gap-1">
                PAN Number
              </span>
              <input
                type="text"
                placeholder="ABCDE1234F"
                value={form.pan}
                onChange={handleChange('pan')}
                maxLength={10}
                autoCapitalize="characters"
                className={getInputClass(Boolean(errors.pan))}
              />
              {errors.pan && <span className="mt-1 text-xs text-red-600">{errors.pan}</span>}
            </label>
          </div>

          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col text-sm text-gray-700">
              <span className="mb-1 inline-flex items-center gap-1">
                Employee Image
              </span>
              <div className="relative h-40 w-full max-w-md overflow-hidden rounded-xl border border-dashed border-gray-300 bg-[#F7E5D5]">
                <label
                  htmlFor="emp-image"
                  className="flex h-full w-full cursor-pointer items-center justify-center text-center text-xs text-gray-500"
                >
                  {empPreviewUrl ? (
                    <img src={empPreviewUrl} alt="Selected employee" className="h-full w-full object-cover" />
                  ) : (
                    <span>
                      Click to browse
                      <br />
                      or drag &amp; drop file
                    </span>
                  )}
                </label>
                <input
                  id="emp-image"
                  ref={empImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange('emp')}
                />
                {empPreviewUrl && (
                  <button
                    type="button"
                    onClick={handleRemoveEmpImage}
                    className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black/80"
                    aria-label="Remove image"
                    title="Remove image"
                  >
                    ×
                  </button>
                )}
              </div>
              {empImage && (
                <p className="mt-2 text-xs text-gray-600">
                  Selected: <span className="font-medium">{empImage.name}</span>
                </p>
              )}
              {errors.empImage && <p className="mt-1 text-xs text-red-600">{errors.empImage}</p>}
            </div>
            <div className="flex flex-col text-sm text-gray-700">
              <span className="mb-1 inline-flex items-center gap-1">
                Aadhar Image
              </span>
              <div className="relative h-40 w-full max-w-md overflow-hidden rounded-xl border border-dashed border-gray-300 bg-[#F7E5D5]">
                <label
                  htmlFor="aadhar-image"
                  className="flex h-full w-full cursor-pointer items-center justify-center text-center text-xs text-gray-500"
                >
                  {aadharPreviewUrl ? (
                    <img src={aadharPreviewUrl} alt="Selected aadhar" className="h-full w-full object-cover" />
                  ) : (
                    <span>
                      Click to browse
                      <br />
                      or drag &amp; drop file
                    </span>
                  )}
                </label>
                <input
                  id="aadhar-image"
                  ref={aadharImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange('aadhar')}
                />
                {aadharPreviewUrl && (
                  <button
                    type="button"
                    onClick={handleRemoveAadharImage}
                    className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black/80"
                    aria-label="Remove image"
                    title="Remove image"
                  >
                    ×
                  </button>
                )}
              </div>
              {aadharImage && (
                <p className="mt-2 text-xs text-gray-600">
                  Selected: <span className="font-medium">{aadharImage.name}</span>
                </p>
              )}
              {errors.aadharImage && (
                <p className="mt-1 text-xs text-red-600">{errors.aadharImage}</p>
              )}
            </div>
            <div className="flex flex-col text-sm text-gray-700">
              <span className="mb-1 inline-flex items-center gap-1">
                PAN Image
              </span>
              <div className="relative h-40 w-full max-w-md overflow-hidden rounded-xl border border-dashed border-gray-300 bg-[#F7E5D5]">
                <label
                  htmlFor="pan-image"
                  className="flex h-full w-full cursor-pointer items-center justify-center text-center text-xs text-gray-500"
                >
                  {panPreviewUrl ? (
                    <img src={panPreviewUrl} alt="Selected PAN" className="h-full w-full object-cover" />
                  ) : (
                    <span>
                      Click to browse
                      <br />
                      or drag &amp; drop file
                    </span>
                  )}
                </label>
                <input
                  id="pan-image"
                  ref={panImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileChange('pan')}
                />
                {panPreviewUrl && (
                  <button
                    type="button"
                    onClick={handleRemovePanImage}
                    className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black/80"
                    aria-label="Remove image"
                    title="Remove image"
                  >
                    ×
                  </button>
                )}
              </div>
              {panImage && (
                <p className="mt-2 text-xs text-gray-600">
                  Selected: <span className="font-medium">{panImage.name}</span>
                </p>
              )}
              {errors.panImage && <p className="mt-1 text-xs text-red-600">{errors.panImage}</p>}
            </div>
          </div>

          <div className="mt-6 flex justify-start gap-5">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex min-w-[140px] items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-800"
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => navigate(listPath)}
              className="inline-flex min-w-[140px] items-center justify-center rounded-lg border border-brand bg-white px-8 py-2.5 text-sm font-semibold text-brand hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

export default AdminEmployeeAdd;
